import { getAuthToken } from '../utils/auth';

const BASE = import.meta.env.VITE_BACKEND_BASE_URL || '';

export interface ChatListItem {
  chatId: string;
  createdAt: string;
  name?: string;
  leagueId?: string;
  playerName?: string;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export async function getAuthStatus(): Promise<Response> {
  const token = getAuthToken();
  if (!token) {
    throw new Error('No authentication token found');
  }
  const res = await fetch(`${BASE}/auth-status`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return res;
}

export async function listChats(params?: { leagueId?: string; playerName?: string }): Promise<ChatListItem[]> {
  const token = getAuthToken();
  if (!token) {
    throw new Error('No authentication token found');
  }
  const queryParams = new URLSearchParams();
  if (params?.leagueId) queryParams.append('leagueId', params.leagueId);
  if (params?.playerName) queryParams.append('playerName', params.playerName);
  const queryString = queryParams.toString();
  const url = queryString ? `${BASE}/chats?${queryString}` : `${BASE}/chats`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`listChats failed: ${res.status}`);
  const data = await res.json();
  return data.chats || [];
}

export async function createChat(params?: { leagueId?: string; playerName?: string }): Promise<{ chatId: string }> {
  const token = getAuthToken();
  if (!token) {
    throw new Error('No authentication token found');
  }
  const body: any = {};
  if (params?.leagueId) body.leagueId = params.leagueId;
  if (params?.playerName) body.playerName = params.playerName;
  
  const res = await fetch(`${BASE}/chats`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`createChat failed: ${res.status}`);
  return res.json();
}

export async function getChatMessages(chatId: string): Promise<ChatMessage[]> {
  const token = getAuthToken();
  if (!token) {
    throw new Error('No authentication token found');
  }
  const res = await fetch(`${BASE}/chats/${chatId}/messages`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`getChatMessages failed: ${res.status}`);
  const data = await res.json();
  return data.messages || [];
}

export async function streamChatMessage(chatId: string, message: string): Promise<ReadableStreamDefaultReader<Uint8Array>> {
  const token = getAuthToken();
  if (!token) {
    throw new Error('No authentication token found');
  }
  const res = await fetch(`${BASE}/chats/${chatId}/messages`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ message }),
  });
  if (res.status === 403) throw Object.assign(new Error('forbidden'), { code: 403 });
  if (res.status === 401) throw Object.assign(new Error('unauthorized'), { code: 401 });
  if (!res.body) throw new Error('No response body');
  return res.body.getReader();
}
